import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";

import { ENTITY_STATUS, PERMISSIONS } from "../../constants";
import {
  ENTITY_DOC_STATUS_COLOR,
  ENTITY_DOC_STATUS_LABELS,
  ENTITY_REQUEST_ERROR_LABELS,
  ENTITY_STATUS_COLOR,
  ENTITY_STATUS_LABELS,
  ENTITY_TYPE_LABELS,
} from "../entities/entityLabels";
import { formatNit } from "../entities/entityLogic";
import EntityContacts from "../entities/EntityContacts";
import EntityDocuments from "../entities/EntityDocuments";
import EntityForm from "../entities/EntityForm";
import EntityLogoUpload from "../submissions/EntityLogoUpload";
import MyEntityMembers from "./MyEntityMembers";
import { getEntityConfig } from "../../services/backend/EntityConfigService";
import {
  addEntityContact,
  deleteEntityContact,
  entityFilePublicUrl,
  getEntity,
  listEntityMembers,
  setEntityLogo,
  updateEntityContact,
} from "../../services/backend/EntityService";

// The company's record seen from the inside: what used to happen on the public
// portal (/submission/entidad/continuar) with an ephemeral session now happens
// here with a real one.
//
// The NIT does NOT come from the URL or from a form: it comes from `entityNit`
// on the user document. A company user cannot even name another entity, and if
// they tried, handleEntity would force their own.
//
// The manager view's components (EntityContacts, EntityDocuments, EntityForm)
// are reused as they are, because the data and the rules are the same; what
// changes is who touches them, and the backend handles that.

const TABS = { DATA: "datos", CONTACTS: "contactos", DOCS: "documentos", TEAM: "personal" };

export default function MyEntityView() {
  const location = useLocation();
  const user = useSelector((state) => state.user.value);
  // Viene en el state del registro público, que aterriza aquí.
  const justRegistered = !!(location.state && location.state.justRegistered);
  const perms = user?.permissions || [];
  const canUpdate = perms.includes(PERMISSIONS.MY_ENTITY.UPDATE);
  const canManageContacts = perms.includes(PERMISSIONS.MY_ENTITY.MANAGE_CONTACTS);
  const canSeeTeam = perms.includes(PERMISSIONS.MY_ENTITY.MANAGE_MEMBERS)
    || perms.includes(PERMISSIONS.MY_ENTITY.INVITE_MEMBER);

  const nit = user?.entityNit || null;

  const [tab, setTab] = useState(TABS.DATA);
  const [entity, setEntity] = useState(null);
  // Las cuentas de la empresa. La pestaña de contactos las necesita para
  // agrupar cada correo y cada teléfono bajo su dueño; LIST_MEMBERS lo permite
  // con MY_ENTITY.VIEW, que tiene todo el mundo, no sólo quien administra.
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [bucket, setBucket] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    if (!nit) { setLoading(false); return; }
    setLoading(true);
    const [record, cfg, roster] = await Promise.all([
      getEntity(nit), getEntityConfig(), listEntityMembers(nit, user),
    ]);
    setEntity(record);
    setMembers(roster.ok ? (roster.data?.members || []) : []);
    setCatalog(cfg.entityDocumentCatalog || []);
    setBucket(cfg.storageBucket || "");
    setLoading(false);
  }, [nit, user]);

  useEffect(() => { load(); }, [load]);

  const contactAction = async (fn) => {
    setContactBusy(true);
    setActionError(null);
    const result = await fn();
    setContactBusy(false);
    if (!result.ok) {
      setActionError(ENTITY_REQUEST_ERROR_LABELS[result.error] || "No se pudo actualizar el contacto.");
      return;
    }
    setEntity((prev) => ({ ...prev, ...result.data }));
  };

  const handleLogo = async (file) => {
    setLogoBusy(true);
    setActionError(null);
    const result = await setEntityLogo({ nit, file, user });
    setLogoBusy(false);
    if (!result.ok) {
      setActionError(ENTITY_REQUEST_ERROR_LABELS[result.error] || "No se pudo actualizar el logo.");
      return;
    }
    setEntity((prev) => ({ ...prev, ...result.data }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // A company account with no `entityNit`, or whose entity was deleted. It
  // should not happen, but if it does, say so with something actionable instead
  // of leaving a blank screen.
  if (!nit || !entity) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>No encontramos tu empresa</Typography>
        <Typography variant="body2" color="text.secondary">
          Tu cuenta no está vinculada a ninguna empresa activa. Comunícate con la
          institución para que revisen tu registro.
        </Typography>
      </Container>
    );
  }

  const completion = entity.completionPercentage || 0;
  const logoUrl = entityFilePublicUrl(bucket, entity.logoPath);
  const field = (label, value) => (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {/* Two elements, not one Box with a switching `component`: an <img> may
            not carry a `children` prop AT ALL, and `{!logo && <Icon/>}` still
            passes one — `false` — once the logo exists. React throws in
            assertValidProps and the whole screen dies, so this only ever broke
            for entities that HAVE a logo. */}
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt=""
            sx={{
              width: 72, height: 40, objectFit: "contain", borderRadius: 1, flexShrink: 0,
              bgcolor: "grey.100",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 72, height: 40, borderRadius: 1, flexShrink: 0, bgcolor: "grey.100",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <BusinessIcon fontSize="small" color="disabled" />
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" noWrap>{entity.legalName}</Typography>
          <Typography variant="body2" color="text.secondary">
            NIT {formatNit(entity.nit, entity.verificationDigit)}
            {entity.entityType ? ` · ${ENTITY_TYPE_LABELS[entity.entityType] || entity.entityType}` : ""}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={ENTITY_STATUS_COLOR[entity.status] || "default"}
          label={ENTITY_STATUS_LABELS[entity.status] || entity.status}
        />
      </Stack>

      {/* Quien acaba de registrar su empresa aterriza aquí, no en el Home: esta
          es la pantalla donde termina lo que el formulario le prometió que
          completaría después. */}
      {justRegistered && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Tu empresa quedó registrada y tu cuenta ya está lista. Completa aquí los
          datos y carga los documentos: con eso el equipo de Extensión puede
          aprobarla.
        </Alert>
      )}

      {/* A company registered through the portal stays PENDING until someone at
          the institution reviews it. It can sign in and complete its data — that
          is what this screen is for — what it cannot do yet is be attached to a
          convenio. Saying so avoids the support call asking what is missing. */}
      {entity.status === ENTITY_STATUS.PENDING && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Tu registro está en revisión. Mientras tanto puedes completar tus datos y
          cargar los documentos que te falten: eso agiliza la aprobación.
        </Alert>
      )}
      {entity.status === ENTITY_STATUS.INACTIVE && entity.rejectionReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tu registro fue devuelto: {entity.rejectionReason}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Datos" value={TABS.DATA} />
        <Tab label="Contactos" value={TABS.CONTACTS} />
        <Tab label="Documentos" value={TABS.DOCS} />
        {canSeeTeam && <Tab label="Personal" value={TABS.TEAM} />}
      </Tabs>

      {tab === TABS.DATA && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Datos de la empresa</Typography>
            {canUpdate && (
              <Button
                size="small" startIcon={<EditIcon />} onClick={() => setFormOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Editar
              </Button>
            )}
          </Stack>
          <Grid container spacing={2}>
            {field("Razón social", entity.legalName)}
            {field("Nombre comercial", entity.tradeName)}
            {field("Sector", entity.sector)}
            {field("País", entity.country)}
            {field("Departamento", entity.department)}
            {field("Ciudad", entity.city)}
            {field("Dirección", entity.address)}
            {field("Sitio web", entity.website)}
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Representante legal</Typography>
          <Grid container spacing={2}>
            {field("Nombre", entity.legalRepName)}
            {field("Documento", entity.legalRepIdNumber)}
            {field("Correo", entity.legalRepEmail)}
            {field("Teléfono", entity.legalRepPhone)}
          </Grid>

          {canUpdate && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Logo</Typography>
              <EntityLogoUpload
                currentUrl={logoUrl}
                alt={entity.legalName}
                disabled={logoBusy}
                onChange={handleLogo}
                onRemove={() => handleLogo(null)}
              />
            </>
          )}
        </Paper>
      )}

      {tab === TABS.CONTACTS && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Contactos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Todo contacto va a nombre de alguien: los de tu equipo debajo de su cuenta,
            los demás como contactos externos. Un correo o un celular solo cuenta cuando
            está verificado; sin verificar no recibe los avisos de vencimiento ni sirve
            para entrar.
          </Typography>
          <EntityContacts
            emails={entity.contactEmails || []}
            phones={entity.contactPhones || []}
            members={members}
            country={entity.country}
            readOnly={!canManageContacts}
            busy={contactBusy}
            onAdd={(channel, contact) =>
              contactAction(() => addEntityContact({ nit, channel, contact, user }))
            }
            onUpdate={(channel, contactId, contact) =>
              contactAction(() => updateEntityContact({ nit, channel, contactId, contact, user }))
            }
            onSetPrimary={(channel, contactId) =>
              contactAction(() => updateEntityContact({ nit, channel, contactId, setPrimary: true, user }))
            }
            onDelete={(channel, contactId) =>
              contactAction(() => deleteEntityContact({ nit, channel, contactId, user }))
            }
          />
        </Paper>
      )}

      {tab === TABS.DOCS && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Documentación</Typography>
            <Chip
              size="small"
              color={ENTITY_DOC_STATUS_COLOR[entity.documentationStatus] || "default"}
              label={ENTITY_DOC_STATUS_LABELS[entity.documentationStatus] || "—"}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, completion)}
              sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
            />
            <Typography variant="body2" sx={{ minWidth: 44, textAlign: "right" }}>{completion}%</Typography>
          </Stack>
          <EntityDocuments
            entity={entity}
            catalog={catalog}
            onChange={(data) => setEntity((prev) => ({ ...prev, ...data }))}
          />
        </Paper>
      )}

      {tab === TABS.TEAM && canSeeTeam && (
        <Paper sx={{ p: 3 }}>
          <MyEntityMembers nit={nit} />
        </Paper>
      )}

      <EntityForm
        open={formOpen}
        entity={entity}
        selfService
        onClose={() => setFormOpen(false)}
        onSaved={(saved) => {
          setFormOpen(false);
          if (saved) setEntity((prev) => ({ ...prev, ...saved }));
        }}
      />
    </Container>
  );
}
